import { type FC, useState } from "react";
import { CollapseArrowIcon } from "../../../protocols/NewFiltersPanelIcons";
import TableHeaderCell from "../shared/TableHeaderCell";
import arrowIcon from "../../../../assets/images/application-summary-arrow.svg";
import closeIcon from "../../../../assets/images/application-summary-close.svg";
import "./style.less";

const DEFAULT_ACCENT_PALETTES: ReadonlyArray<readonly string[]> = [
  ["#C6E6FF", "#82CFFF", "#C3EAD1", "#A3A9B9"],
  ["#90D6AA", "#5EBB7F", "#31B4F2", "#A3A9B9"],
];

export interface ApplicationSummaryRow {
  id: string;
  plot: string | number;
  treatment: string;
  accentColor?: string;
}

export interface ApplicationSummaryGroup {
  id: string;
  label: string;
  rows: ApplicationSummaryRow[];
  defaultExpanded?: boolean;
}

export interface ApplicationSummaryColumn {
  id: string;
  label: string;
  groups: ApplicationSummaryGroup[];
}

export interface ApplicationSummaryProps {
  title: string;
  groups: ApplicationSummaryGroup[];
  applicationColumns?: ApplicationSummaryColumn[];
  onClose: () => void;
  plotColumnLabel?: string;
  treatmentColumnLabel?: string;
}

interface GroupSectionProps {
  group: ApplicationSummaryGroup;
  groupIndex: number;
  plotColumnLabel: string;
  treatmentColumnLabel: string;
  applicationColumns?: ApplicationSummaryColumn[];
}

const GroupSection: FC<GroupSectionProps> = ({
  group,
  groupIndex,
  plotColumnLabel,
  treatmentColumnLabel,
  applicationColumns,
}) => {
  const [expanded, setExpanded] = useState(group.defaultExpanded ?? true);
  const tableId = `application-summary-${group.id}`;

  return (
    <section className="application-summary__group">
      <button
        type="button"
        className="application-summary__group-toggle"
        aria-expanded={expanded}
        aria-controls={tableId}
        onClick={() => {
          setExpanded((previousExpanded) => !previousExpanded);
        }}
      >
        <CollapseArrowIcon
          className={`application-summary__group-icon ${
            expanded
              ? "application-summary__group-icon--expanded"
              : "application-summary__group-icon--collapsed"
          }`}
        />
        <span>{group.label}</span>
      </button>
      {expanded && (
        <div
          id={tableId}
          className={`application-summary__table ${
            applicationColumns ? "application-summary__table--multiple" : ""
          }`}
          role="table"
          aria-label={`${group.label} application assignments`}
        >
          <div className="application-summary__header" role="row">
            <TableHeaderCell
              classPrefix="application-summary"
              label={plotColumnLabel}
              unit=""
              columnClassName="application-summary__column--plot"
              showIcons={false}
            />
            {applicationColumns ? (
              applicationColumns.map((application) => (
                <TableHeaderCell
                  key={application.id}
                  classPrefix="application-summary"
                  label={application.label}
                  unit=""
                  columnClassName="application-summary__column--application"
                  showIcons={false}
                />
              ))
            ) : (
              <TableHeaderCell
                classPrefix="application-summary"
                label={treatmentColumnLabel}
                unit=""
                columnClassName="application-summary__column--treatment"
                showIcons={false}
              />
            )}
          </div>
          <div className="application-summary__body" role="rowgroup">
            {group.rows.map((row, rowIndex) => {
              const palette =
                DEFAULT_ACCENT_PALETTES[
                  groupIndex % DEFAULT_ACCENT_PALETTES.length
                ];
              const accentColor =
                row.accentColor ?? palette[rowIndex % palette.length];

              return (
                <div
                  key={row.id}
                  className="application-summary__row"
                  role="row"
                >
                  <div
                    className="application-summary__cell application-summary__column--plot"
                    role="cell"
                  >
                    <span
                      className="application-summary__accent"
                      style={{ backgroundColor: accentColor }}
                      aria-hidden="true"
                    />
                    <span>{row.plot}</span>
                  </div>
                  {applicationColumns ? (
                    applicationColumns.map((application) => {
                      const applicationRow = application.groups
                        .find(({ id }) => id === group.id)
                        ?.rows.find(({ id }) => id === row.id);

                      return (
                        <div
                          key={`${application.id}-${row.id}`}
                          className="application-summary__cell application-summary__column--application"
                          role="cell"
                        >
                          <span
                            className="application-summary__accent"
                            style={{
                              backgroundColor:
                                applicationRow?.accentColor ?? "#A3A9B9",
                            }}
                            aria-hidden="true"
                          />
                          <span>{applicationRow?.treatment ?? ""}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      className="application-summary__cell application-summary__column--treatment"
                      role="cell"
                    >
                      {row.treatment}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

const ApplicationSummary: FC<ApplicationSummaryProps> = ({
  title,
  groups,
  applicationColumns,
  onClose,
  plotColumnLabel = "Plot",
  treatmentColumnLabel = "Treatment",
}) => (
  <div
    className={`application-summary ${
      applicationColumns ? "application-summary--multiple" : ""
    }`}
  >
    {applicationColumns && (
      <span className="application-summary__arrow" aria-hidden="true">
        <img src={arrowIcon} alt="" />
      </span>
    )}
    <header className="application-summary__header-row">
      <h2 className="application-summary__title">{title}</h2>
      <button
        type="button"
        className="application-summary__close"
        aria-label={`Close ${title}`}
        onClick={onClose}
      >
        <img
          className="application-summary__close-icon"
          src={closeIcon}
          alt=""
          aria-hidden="true"
        />
      </button>
    </header>
    <div
      className={`application-summary__groups ${
        applicationColumns ? "application-summary__groups--multiple" : ""
      }`}
    >
      {groups.map((group, groupIndex) => (
        <GroupSection
          key={group.id}
          group={group}
          groupIndex={groupIndex}
          plotColumnLabel={plotColumnLabel}
          treatmentColumnLabel={treatmentColumnLabel}
          applicationColumns={applicationColumns}
        />
      ))}
    </div>
  </div>
);

export default ApplicationSummary;
